import React, { useEffect, useState } from "react";
import { Link } from "@/lib/nav";
import { useTranslation } from "react-i18next";
import { Hexagon } from "lucide-react";

const COMPLIANCE = ["GDPR", "NIS2", "eIDAS", "AGID", "ISO 27001"];

// Impronta della build servita in questo momento: se non c'è manifest firmato non si
// scrive niente, perché una riga vuota è meglio di una rassicurazione senza prove.
function BuildFingerprint() {
  const [m, setM] = useState(null);
  useEffect(() => {
    fetch("/web-manifest.json", { cache: "no-store" })
      .then((r) => (r.ok ? r.json() : null)).then(setM).catch(() => {});
  }, []);
  if (!m?.root) return null;
  return (
    <Link to="/firma-web" data-testid="footer-build-fingerprint" className="font-mono text-[11px] text-[#555] hover:text-white">
      v{m.version} · <span className="lp-c">{m.root.slice(0, 12)}</span>
    </Link>
  );
}

export default function Footer() {
  const { t } = useTranslation();
  return (
    <footer className="border-t border-[#262626] bg-[#070707]" data-testid="site-footer">
      <div className="mx-auto max-w-[1400px] px-5 py-14 lg:px-8">
        <div className="grid gap-10 md:grid-cols-[1.5fr_1fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-2.5">
              <Hexagon className="h-7 w-7 lp-c" strokeWidth={1.5} />
              <span className="font-display text-lg font-700">LATTICE NETWORK</span>
            </div>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-[#a3a3a3]">
              {t("footer.tagline")}
            </p>
            <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-[#555]">{t("footer.designedFor")}</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {COMPLIANCE.map((c) => (
                <span key={c} className="border border-white/10 px-2 py-1 font-mono text-[10px] uppercase tracking-wider text-[#666]">
                  {c}
                </span>
              ))}
            </div>
          </div>

          <div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-[#666]">{t("footer.colNetwork")}</div>
            <ul className="mt-4 space-y-2.5 text-sm text-[#a3a3a3]">
              <li><Link to="/chi-siamo" className="hover:text-white" data-testid="footer-chi-siamo">{t("footer.about")}</Link></li>
              <li><Link to="/casi-duso" className="hover:text-white" data-testid="footer-casi-duso">{t("footer.useCases")}</Link></li>
              <li><Link to="/prezzi" className="hover:text-white" data-testid="footer-prezzi">{t("footer.pricing")}</Link></li>
              <li><Link to="/demo" className="hover:text-white" data-testid="footer-demo">{t("footer.demo")}</Link></li>
              <li><Link to="/per-il-cio" className="hover:text-white" data-testid="footer-cio">{t("footer.forCio")}</Link></li>
              <li><Link to="/per-il-ciso" className="hover:text-white" data-testid="footer-ciso">{t("footer.forCiso")}</Link></li>
              <li><Link to="/per-il-ceo" className="hover:text-white" data-testid="footer-ceo">{t("footer.forCeo")}</Link></li>
              <li><Link to="/sviluppatori" className="hover:text-white" data-testid="footer-sviluppatori">{t("footer.developers")}</Link></li>
              <li><Link to="/explorer" className="hover:text-white">{t("footer.explorer")}</Link></li>
              <li><Link to="/verifica" className="hover:text-white" data-testid="footer-verifica">{t("footer.verifica", "Verifica pubblica")}</Link></li>
            </ul>
          </div>

          <div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-[#666]">{t("footer.colServices")}</div>
            <ul className="mt-4 space-y-2.5 text-sm text-[#a3a3a3]">
              <li><Link to="/certify" className="hover:text-white">{t("footer.certify")}</Link></li>
              <li><Link to="/pulse" className="hover:text-white">{t("footer.pulse")}</Link></li>
              <li><Link to="/login" className="hover:text-white">{t("footer.secureAccess")}</Link></li>
            </ul>
          </div>

          <div>
            <div className="font-mono text-[11px] uppercase tracking-widest text-[#666]">{t("footer.colDocs")}</div>
            <ul className="mt-4 space-y-2.5 text-sm text-[#a3a3a3]">
              <li><Link to="/whitepaper" className="hover:text-white" data-testid="footer-whitepaper">{t("footer.whitepaper")}</Link></li>
              <li><Link to="/yellowpaper" className="hover:text-white" data-testid="footer-yellowpaper">{t("footer.yellowpaper")}</Link></li>
              <li><Link to="/threat-model" className="hover:text-white" data-testid="footer-threat-model">{t("footer.threatModel")}</Link></li>
              <li><Link to="/audit/ledger" className="hover:text-white" data-testid="footer-audit">{t("footer.internalAudit")}</Link></li>
              <li><Link to="/audit/red-team" className="hover:text-white" data-testid="footer-redteam">Red Team</Link></li>
              <li><Link to="/firma-web" className="hover:text-white" data-testid="footer-firma-web">{t("footer.webSignature")}</Link></li>
              <li><Link to="/guida-nodo" className="hover:text-white" data-testid="footer-guida-nodo">{t("footer.nodeGuide")}</Link></li>
              <li><Link to="/prova-qbft" className="hover:text-white" data-testid="footer-prova-qbft">{t("footer.qbftProof", "Dimostrazione QBFT")}</Link></li>
              <li>
                <a href="https://github.com/Jabo86/lattice-android" target="_blank" rel="noopener noreferrer"
                   className="hover:text-white" data-testid="footer-codice-sorgente">
                  {t("footer.sourceCode", "Codice sorgente ↗")}
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-10 flex flex-wrap gap-x-5 gap-y-2 border-t border-[#1a1a1a] pt-6 font-mono text-[11px] uppercase tracking-wider text-[#666]" data-testid="footer-legal">
          <Link to="/legale" className="hover:text-white">{t("footer.legalArea")}</Link>
          <Link to="/conformita" className="hover:text-white" data-testid="footer-conformita">{t("footer.compliance")}</Link>
          <Link to="/documenti" className="hover:text-white" data-testid="footer-documenti">Documenti</Link>
          <Link to="/termini" className="hover:text-white" data-testid="footer-termini">{t("footer.terms")}</Link>
          <Link to="/privacy" className="hover:text-white" data-testid="footer-privacy">{t("footer.privacy")}</Link>
          <Link to="/dpa" className="hover:text-white">{t("footer.dpa")}</Link>
          <Link to="/cookie" className="hover:text-white">{t("footer.cookie")}</Link>
          <Link to="/sla" className="hover:text-white">{t("footer.sla")}</Link>
          <Link to="/licenza" className="hover:text-white">{t("footer.eula")}</Link>
          <Link to="/uso-accettabile" className="hover:text-white">{t("footer.aup")}</Link>
        </div>

        <div className="mt-8 flex flex-col items-start justify-between gap-3 border-t border-[#262626] pt-6 sm:flex-row sm:items-center">
          <p className="font-mono text-[11px] text-[#555]">
            © {new Date().getFullYear()} Lattice Network · chain: lattice-sovereign-it-prod
          </p>
          <p className="flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[11px] text-[#555]">
            <span>{t("footer.backend")}: <span className="lp-c">rust</span> · 0 python · 0 unsafe</span>
            <BuildFingerprint />
          </p>
        </div>
      </div>
    </footer>
  );
}
